/**
 * useQuickBillStore — the invoice-style Quick Bill form's draft.
 *
 * Lives outside the component so the form survives the trip to the payment
 * screen and back (CheckoutScreen replaces the form while payment is taken).
 * The bill date is NOT here: it stays in useCartStore.billDate, which the sale
 * writer and the checkout banner already read.
 *
 * Not persisted: a half-typed bill is not worth surviving a reload for, and a
 * stale draft reappearing on another day is its own surprise.
 */
import { create } from 'zustand';
import type { DispatchDetails, DispatchKey } from '@/billing/invoiceExtras';
import { emptyRow, isBlankRow, type QuickBillRow } from '@/billing/quickBillForm';
import type { TypedBuyer } from '@/billing/billToParty';

const EMPTY_BUYER: TypedBuyer = { customerId: null, supplierId: null, name: '', address: '', gstin: '', phone: '' };

interface QuickBillState {
  buyer: TypedBuyer;
  details: DispatchDetails;
  /** Free-form terms, printed after the dispatch lines. */
  freeTerms: string;
  /** Printed as "Notes" (invoices.notes). */
  remarks: string;
  /** Always ends with one blank row — the next line to type into. */
  rows: QuickBillRow[];
  /** The Rate column is before GST unless this is on. */
  ratesIncludeGst: boolean;
  /** null = follow the buyer's GSTIN; true/false = the operator's override. */
  interStateOverride: boolean | null;

  /** Typing in the buyer fields un-picks a picked record: it's a new buyer now. */
  setBuyerField: (field: 'name' | 'address' | 'gstin' | 'phone', value: string) => void;
  pickBuyer: (buyer: TypedBuyer) => void;
  setDetail: (key: DispatchKey, value: string) => void;
  setFreeTerms: (v: string) => void;
  setRemarks: (v: string) => void;
  updateRow: (key: string, patch: Partial<QuickBillRow>) => void;
  removeRow: (key: string) => void;
  setRatesIncludeGst: (v: boolean) => void;
  setInterStateOverride: (v: boolean | null) => void;
  reset: () => void;
}

/** Keep exactly one blank row at the end. */
function withTrailingBlank(rows: QuickBillRow[]): QuickBillRow[] {
  const trimmed = [...rows];
  while (trimmed.length > 1 && isBlankRow(trimmed[trimmed.length - 1]) && isBlankRow(trimmed[trimmed.length - 2])) {
    trimmed.pop();
  }
  if (trimmed.length === 0 || !isBlankRow(trimmed[trimmed.length - 1])) trimmed.push(emptyRow());
  return trimmed;
}

const initial = () => ({
  buyer: { ...EMPTY_BUYER },
  details: {},
  freeTerms: '',
  remarks: '',
  rows: [emptyRow()],
  ratesIncludeGst: false,
  interStateOverride: null,
});

export const useQuickBillStore = create<QuickBillState>((set) => ({
  ...initial(),

  setBuyerField: (field, value) =>
    set((s) => ({
      buyer: {
        ...s.buyer,
        [field]: value,
        // Renaming a picked buyer makes it someone else; editing their address,
        // GSTIN or phone does not (those fill blanks on the saved record).
        ...(field === 'name' ? { customerId: null, supplierId: null } : {}),
      },
    })),
  pickBuyer: (buyer) => set({ buyer, interStateOverride: null }),
  setDetail: (key, value) => set((s) => ({ details: { ...s.details, [key]: value } })),
  setFreeTerms: (freeTerms) => set({ freeTerms }),
  setRemarks: (remarks) => set({ remarks }),
  updateRow: (key, patch) =>
    set((s) => ({ rows: withTrailingBlank(s.rows.map((r) => (r.key === key ? { ...r, ...patch } : r))) })),
  removeRow: (key) => set((s) => ({ rows: withTrailingBlank(s.rows.filter((r) => r.key !== key)) })),
  setRatesIncludeGst: (ratesIncludeGst) => set({ ratesIncludeGst }),
  setInterStateOverride: (interStateOverride) => set({ interStateOverride }),
  reset: () => set(initial()),
}));
